import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import MoreVert from '@mui/icons-material/MoreVert';
import Close from '@mui/icons-material/Close';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import cn from 'classnames';
import {exchangeTimezones, type ExchangeTimezone} from './timezones.js';
import {useNow} from './use-now.js';
import './timezone-clocks.scss';

// Map of class names from `timezone-clocks.scss` which provides shorter names to type (especially for those referenced multiple times) and
// also allows for easier refactoring of class names in the future since we can update them all in one place.
const tzc = {
  root: 'timezone-clocks',
  menuControlWrap: 'timezone-clocks__menu-control-wrap',
  addButton: 'timezone-clocks__add-button',
  hourFormatToggle: 'timezone-clocks__hour-format-toggle',
  hourFormatOption: 'timezone-clocks__hour-format-option',
  hourFormatOptionPressed: 'timezone-clocks__hour-format-option--pressed',
  menuHourFormatWrap: 'timezone-clocks__menu-hour-format-wrap',
  clocksShell: 'timezone-clocks__clocks-shell',
  clocksShellScrollable: 'timezone-clocks__clocks-shell--scrollable',
  clocksScroller: 'timezone-clocks__clocks-scroller',
  clocksScrollContent: 'timezone-clocks__clocks-scroll-content',
  clocksRow: 'timezone-clocks__clocks-row',
  clocksScrollbarWrap: 'timezone-clocks__clocks-scrollbar-wrap',
  clocksScrollbar: 'timezone-clocks__clocks-scrollbar',
  clock: 'timezone-clocks__clock',
  clockDragging: 'timezone-clocks__clock--dragging',
  time: 'timezone-clocks__time',
  location: 'timezone-clocks__location',
  remove: 'timezone-clocks__remove',
  menuItem: 'timezone-clocks__menu-item',
  menuCheckbox: 'timezone-clocks__menu-checkbox',
  menuDivider: 'timezone-clocks__menu-divider',
} as const;

const storageKey = 'toolbar.timezoneClocks.selected';
const hour12StorageKey = 'toolbar.timezoneClocks.hour12';
const defaultSelectedIds = ['minneapolis'];
const clockWidthPx = 118;
const clockGapPx = 8;
const clockScrollStepPx = clockWidthPx + clockGapPx;

type ScrollMetrics = {
  maxScrollLeft: number;
  scrollLeft: number;
};

function isBrowserEnvironment() {
  return globalThis.window !== undefined;
}

function safeParseBoolean(value: string | undefined): boolean | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (value === 'true') {
    return true;
  }

  if (value === 'false') {
    return false;
  }

  return undefined;
}

function exchangeFromLabel(label: string) {
  const match = /\(([^)]+)\)\s*$/.exec(label);
  return match?.[1];
}

function safeParseSelected(value: string | undefined): string[] {
  if (!value) {
    return [];
  }

  try {
    const parsed = JSON.parse(value) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.filter((x): x is string => typeof x === 'string');
  } catch {
    return [];
  }
}

function sanitizeSelectedIds(value: string[]): string[] {
  const validIds = new Set(exchangeTimezones.map(tz => tz.id));
  return value.filter((id, index) => validIds.has(id) && value.indexOf(id) === index);
}

function formatTime(now: Date, timeZone: string, options?: {hour12?: boolean}) {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: options?.hour12 ?? false,
  }).format(now);
}

function moveItem<T>(items: T[], fromIndex: number, toIndex: number) {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) {
    return items;
  }

  const next = [...items];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

export function TimezoneClocks() {
  const now = useNow(1000);
  const rootRef = useRef<HTMLDivElement>(null);
  const clocksScrollerRef = useRef<HTMLDivElement>(null);
  const clocksRowRef = useRef<HTMLDivElement>(null);
  const lastDragPointRef = useRef<{clientX: number; clientY: number} | undefined>(undefined);
  const draggingIdRef = useRef<string | undefined>(undefined);
  const previewInsertIndexRef = useRef<number | undefined>(undefined);
  const dragMetricsRef = useRef<{offsetX: number; width: number} | undefined>(undefined);
  const didDropRef = useRef(false);
  const selectedIdsRef = useRef<string[]>([]);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | undefined>(undefined);
  const [menuAnchorPosition, setMenuAnchorPosition] = useState<{top: number; left: number} | undefined>(undefined);
  const menuOpen = Boolean(anchorElement);
  const [draggingId, setDraggingId] = useState<string | undefined>(undefined);
  const [previewInsertIndex, setPreviewInsertIndex] = useState<number | undefined>(undefined);
  const [scrollMetrics, setScrollMetrics] = useState<ScrollMetrics>({
    maxScrollLeft: 0,
    scrollLeft: 0,
  });

  const [use12Hour, setUse12Hour] = useState<boolean>(() => {
    if (!isBrowserEnvironment()) {
      return false;
    }

    return safeParseBoolean(globalThis.localStorage.getItem(hour12StorageKey) ?? undefined) ?? false;
  });

  const [selectedIds, setSelectedIds] = useState<string[]>(() => {
    if (!isBrowserEnvironment()) {
      return defaultSelectedIds;
    }

    const rawSelected = globalThis.localStorage.getItem(storageKey);
    if (rawSelected === null) {
      return defaultSelectedIds;
    }

    return sanitizeSelectedIds(safeParseSelected(rawSelected));
  });

  useEffect(() => {
    if (!isBrowserEnvironment()) {
      return;
    }

    globalThis.localStorage.setItem(storageKey, JSON.stringify(selectedIds));
  }, [selectedIds]);

  useEffect(() => {
    selectedIdsRef.current = selectedIds;
  }, [selectedIds]);

  useEffect(() => {
    if (!isBrowserEnvironment()) {
      return;
    }

    globalThis.localStorage.setItem(hour12StorageKey, String(use12Hour));
  }, [use12Hour]);

  const selectedTimezones = useMemo(() => {
    const byId = new Map(exchangeTimezones.map(tz => [tz.id, tz] as const));
    return selectedIds.map(id => byId.get(id)).filter(Boolean) as ExchangeTimezone[];
  }, [selectedIds]);

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const menuTimezones = useMemo(
    () => [...exchangeTimezones].sort((a, b) => a.location.localeCompare(b.location)),
    [],
  );

  const syncScrollMetrics = useCallback(() => {
    const scrollElement = clocksScrollerRef.current;
    const rowElement = clocksRowRef.current;
    if (!scrollElement) {
      return;
    }

    const viewportWidth = scrollElement.clientWidth;
    const fromScrollPort = scrollElement.scrollWidth - scrollElement.clientWidth;
    const contentWidth = rowElement
      ? Math.max(rowElement.scrollWidth, rowElement.offsetWidth)
      : 0;
    const fromRow = contentWidth - viewportWidth;
    const overflow = Math.max(fromScrollPort, fromRow);
    const maxScrollLeft = Math.max(0, Math.round(overflow));

    const {scrollLeft: currentScrollLeft} = scrollElement;
    if (currentScrollLeft > maxScrollLeft) {
      scrollElement.scrollLeft = maxScrollLeft;
    }

    const {scrollLeft} = scrollElement;

    setScrollMetrics(previousMetrics => {
      const nextMetrics = {
        maxScrollLeft,
        scrollLeft,
      };

      if (
        previousMetrics.maxScrollLeft === nextMetrics.maxScrollLeft
        && previousMetrics.scrollLeft === nextMetrics.scrollLeft
      ) {
        return previousMetrics;
      }

      return nextMetrics;
    });
  }, []);

  useLayoutEffect(() => {
    syncScrollMetrics();
  }, [selectedTimezones.length, syncScrollMetrics]);

  useEffect(() => {
    const scrollElement = clocksScrollerRef.current;
    const rowElement = clocksRowRef.current;
    if (!scrollElement) {
      return;
    }

    syncScrollMetrics();

    if (typeof ResizeObserver === 'undefined') {
      const onResize = () => {
        syncScrollMetrics();
      };

      globalThis.window?.addEventListener('resize', onResize);
      return () => {
        globalThis.window?.removeEventListener('resize', onResize);
      };
    }

    const observer = new ResizeObserver(() => {
      syncScrollMetrics();
    });
    observer.observe(scrollElement);
    if (rowElement) {
      observer.observe(rowElement);
    }

    return () => {
      observer.disconnect();
    };
  }, [selectedTimezones.length, syncScrollMetrics]);

  function openMenu(event: React.MouseEvent<HTMLElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    setMenuAnchorPosition({
      top: rect.bottom,
      left: rect.left,
    });
    setAnchorElement(event.currentTarget);
  }

  function closeMenu() {
    setAnchorElement(undefined);
    setMenuAnchorPosition(undefined);
  }

  function toggleClock(id: string) {
    setSelectedIds(previous => (previous.includes(id)
      ? previous.filter(x => x !== id)
      : [...previous, id]));
  }

  const allSelected = useMemo(
    () => exchangeTimezones.every(tz => selectedIdSet.has(tz.id)),
    [selectedIdSet],
  );

  const someSelected = selectedIds.length > 0;

  function toggleSelectAll() {
    setSelectedIds(allSelected ? [] : menuTimezones.map(tz => tz.id));
  }

  function removeClock(id: string) {
    setSelectedIds(previous => previous.filter(x => x !== id));
  }

  function isDraggedOutsideToolbar(event: React.DragEvent) {
    const root = rootRef.current;
    if (!root) {
      return false;
    }

    const rect = root.getBoundingClientRect();
    const thresholdPx = 12;

    const clientY = event.clientY ?? lastDragPointRef.current?.clientY ?? 0;

    return clientY < rect.top - thresholdPx || clientY > rect.bottom + thresholdPx;
  }

  function reorderClocks(fromId: string, toId: string) {
    if (fromId === toId) {
      return;
    }

    const fromIndex = selectedIdsRef.current.indexOf(fromId);
    const toIndex = selectedIdsRef.current.indexOf(toId);
    if (fromIndex === -1 || toIndex === -1) {
      return;
    }

    setSelectedIds(previous => moveItem(previous, fromIndex, toIndex));
  }

  function reorderClocksByInsertIndex(fromId: string, toIndex: number, sourceIds = selectedIdsRef.current) {
    const fromIndex = sourceIds.indexOf(fromId);
    if (fromIndex === -1) {
      return;
    }

    const next = [...sourceIds];
    const [removed] = next.splice(fromIndex, 1);
    const boundedTarget = Math.max(0, Math.min(toIndex, next.length));
    next.splice(boundedTarget, 0, removed);
    setSelectedIds(next);
  }

  function getInsertIndexFromHover(overId: string, clientX: number, target: HTMLDivElement, sourceId: string): number | undefined {
    const overIndex = selectedIdsRef.current.indexOf(overId);
    const sourceIndex = selectedIdsRef.current.indexOf(sourceId);
    if (overIndex === -1 || sourceIndex === -1) {
      return undefined;
    }

    const rect = target.getBoundingClientRect();
    if (rect.width <= 0) {
      return undefined;
    }

    const dragMetrics = dragMetricsRef.current;
    let isAfterThreshold: boolean;
    if (dragMetrics) {
      const draggedLeft = clientX - dragMetrics.offsetX;
      const draggedRight = draggedLeft + dragMetrics.width;
      const overlapWidth = Math.max(0, Math.min(draggedRight, rect.right) - Math.max(draggedLeft, rect.left));
      const overlapRatio = overlapWidth / rect.width;
      if (overlapRatio < 0.3) {
        return undefined;
      }

      isAfterThreshold = sourceIndex < overIndex;
    } else {
      isAfterThreshold = (clientX - rect.left) >= (rect.width * 0.3);
    }

    let insertIndex = overIndex + (isAfterThreshold ? 1 : 0);
    if (sourceIndex < insertIndex) {
      insertIndex -= 1;
    }

    return Math.max(0, Math.min(insertIndex, selectedIds.length - 1));
  }

  function getClockOffset(index: number): number {
    if (!draggingId || previewInsertIndex === undefined) {
      return 0;
    }

    const sourceIndex = selectedIds.indexOf(draggingId);
    if (sourceIndex === -1 || previewInsertIndex === sourceIndex || index === sourceIndex) {
      return 0;
    }

    if (previewInsertIndex > sourceIndex && index > sourceIndex && index <= previewInsertIndex) {
      return -clockScrollStepPx;
    }

    if (previewInsertIndex < sourceIndex && index >= previewInsertIndex && index < sourceIndex) {
      return clockScrollStepPx;
    }

    return 0;
  }

  function commitReorder(fromId: string, fallbackToId?: string) {
    const insertIndex = previewInsertIndexRef.current;
    if (insertIndex !== undefined) {
      reorderClocksByInsertIndex(fromId, insertIndex, selectedIdsRef.current);
      return;
    }

    if (fallbackToId && fromId !== fallbackToId) {
      reorderClocks(fromId, fallbackToId);
    }
  }

  const {maxScrollLeft, scrollLeft} = scrollMetrics;
  const isScrollable = maxScrollLeft >= 2;
  const rangeValue = Math.min(scrollLeft, maxScrollLeft);

  function handleScrollbarChange(event: ChangeEvent<HTMLInputElement>) {
    const scrollElement = clocksScrollerRef.current;
    if (!scrollElement) {
      return;
    }

    scrollElement.scrollLeft = Number(event.currentTarget.value);
    syncScrollMetrics();
  }

  return (
    <div ref={rootRef} className={tzc.root} data-testid='timezone-clocks'>
      <div
        className={cn(tzc.clocksShell, {[tzc.clocksShellScrollable]: isScrollable})}
        data-testid='timezone-clocks-shell'
      >
        <div
          ref={clocksScrollerRef}
          className={tzc.clocksScroller}
          data-testid='timezone-clocks-scroller'
          onDragOver={event => {
            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';
          }}
          onDrop={event => {
            event.preventDefault();
            const transferId = event.dataTransfer.getData('text/plain');
            const fromId = transferId.length > 0 ? transferId : (draggingIdRef.current ?? draggingId);
            if (fromId) {
              didDropRef.current = true;
              commitReorder(fromId);
            }

            setDraggingId(undefined);
            draggingIdRef.current = undefined;
            previewInsertIndexRef.current = undefined;
            dragMetricsRef.current = undefined;
            setPreviewInsertIndex(undefined);
            lastDragPointRef.current = undefined;
          }}
          onScroll={() => {
            syncScrollMetrics();
          }}
          onWheel={event => {
            if (!event.shiftKey) {
              return;
            }

            event.preventDefault();
            const element = clocksScrollerRef.current;
            if (!element) {
              return;
            }

            const delta = event.deltaY + event.deltaX;
            element.scrollLeft += delta;

            requestAnimationFrame(() => {
              syncScrollMetrics();
            });
          }}
        >
          <div className={tzc.clocksScrollContent}>
            <div
              ref={clocksRowRef}
              className={tzc.clocksRow}
              data-testid='timezone-clocks-row'
            >
              {selectedTimezones.map((tz, index) => (
                <div
                  key={tz.id}
                  className={cn(tzc.clock, {[tzc.clockDragging]: draggingId === tz.id})}
                  data-testid={`timezone-clock-${tz.id}`}
                  draggable
                  style={{transform: `translateX(${getClockOffset(index)}px)`}}
                  onDragStart={event => {
                    setDraggingId(tz.id);
                    draggingIdRef.current = tz.id;
                    didDropRef.current = false;
                    setPreviewInsertIndex(undefined);
                    previewInsertIndexRef.current = undefined;
                    const rect = event.currentTarget.getBoundingClientRect();
                    const width = rect.width > 0 ? rect.width : clockWidthPx;
                    const rawOffsetX = event.clientX - rect.left;
                    const fallbackOffsetX = width / 2;
                    const offsetX = Number.isFinite(rawOffsetX)
                      ? Math.min(width, Math.max(0, rawOffsetX))
                      : fallbackOffsetX;
                    dragMetricsRef.current = {offsetX, width};
                    lastDragPointRef.current = {clientX: event.clientX, clientY: event.clientY};
                    event.dataTransfer.setData('text/plain', tz.id);
                    event.dataTransfer.effectAllowed = 'move';
                  }}
                  onDrag={event => {
                    lastDragPointRef.current = {clientX: event.clientX, clientY: event.clientY};
                  }}
                  onDragEnd={event => {
                    if (isDraggedOutsideToolbar(event)) {
                      removeClock(tz.id);
                    } else if (!didDropRef.current) {
                      const fromId = draggingIdRef.current ?? draggingId ?? tz.id;
                      if (fromId) {
                        commitReorder(fromId);
                      }
                    }

                    didDropRef.current = false;
                    setDraggingId(undefined);
                    draggingIdRef.current = undefined;
                    previewInsertIndexRef.current = undefined;
                    dragMetricsRef.current = undefined;
                    setPreviewInsertIndex(undefined);
                    lastDragPointRef.current = undefined;
                  }}
                  onDragOver={event => {
                    event.preventDefault();
                    event.dataTransfer.dropEffect = 'move';
                    const fromId = draggingIdRef.current ?? draggingId ?? event.dataTransfer.getData('text/plain');
                    if (!fromId) {
                      return;
                    }

                    // Ignore hover calculations on the dragged clock itself so we keep
                    // the last valid preview position from neighboring clocks.
                    if (fromId === tz.id) {
                      return;
                    }

                    const insertIndex = getInsertIndexFromHover(tz.id, event.clientX, event.currentTarget, fromId);
                    if (insertIndex !== undefined) {
                      previewInsertIndexRef.current = insertIndex;
                      setPreviewInsertIndex(insertIndex);
                    }
                  }}
                  onDrop={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    const transferId = event.dataTransfer.getData('text/plain');
                    const fromId = transferId.length > 0 ? transferId : (draggingIdRef.current ?? draggingId);
                    if (fromId) {
                      didDropRef.current = true;
                      commitReorder(fromId, tz.id);
                    }

                    setDraggingId(undefined);
                    draggingIdRef.current = undefined;
                    previewInsertIndexRef.current = undefined;
                    dragMetricsRef.current = undefined;
                    setPreviewInsertIndex(undefined);
                    lastDragPointRef.current = undefined;
                  }}
                >
                  <IconButton
                    className={tzc.remove}
                    size='small'
                    aria-label={`Remove ${tz.location} clock`}
                    onClick={() => {
                      removeClock(tz.id);
                    }}
                  >
                    <Close fontSize='inherit' />
                  </IconButton>
                  <div className={tzc.time} data-testid={`timezone-clock-time-${tz.id}`}>
                    {formatTime(now, tz.timeZone, {hour12: use12Hour})}
                  </div>
                  <div className={tzc.location}>
                    {tz.location}
                    {exchangeFromLabel(tz.label) ? ` (${exchangeFromLabel(tz.label)})` : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {isScrollable
          ? (
            <div className={tzc.clocksScrollbarWrap}>
              <input
                aria-label='Scroll timezone clocks'
                className={tzc.clocksScrollbar}
                data-testid='timezone-clocks-scrollbar'
                max={maxScrollLeft}
                min={0}
                onChange={handleScrollbarChange}
                onInput={handleScrollbarChange}
                step={1}
                type='range'
                value={rangeValue}
              />
            </div>
          )
          : null}
      </div>

      <div className={tzc.menuControlWrap}>
        <Tooltip title='Timezones/Exchanges'>
          <IconButton
            className={tzc.addButton}
            size='small'
            aria-label='Add timezone clock'
            onClick={openMenu}
          >
            <MoreVert fontSize='inherit' />
          </IconButton>
        </Tooltip>
      </div>

      <Menu
        id='timezone-clocks-menu'
        anchorEl={anchorElement ?? null}
        anchorReference={menuAnchorPosition ? 'anchorPosition' : 'anchorEl'}
        anchorPosition={menuAnchorPosition ?? undefined}
        open={menuOpen}
        onClose={closeMenu}
        MenuListProps={{'aria-label': 'Timezone clocks', dense: true}}
      >
        <ListSubheader disableSticky className={tzc.menuHourFormatWrap}>
          <div
            onMouseDown={event => {
              event.stopPropagation();
            }}
            onClick={event => {
              event.stopPropagation();
            }}
          >
            <div
              className={tzc.hourFormatToggle}
              role='group'
              aria-label='Clock time format'
            >
              <button
                type='button'
                className={cn(tzc.hourFormatOption, {[tzc.hourFormatOptionPressed]: !use12Hour})}
                aria-pressed={!use12Hour}
                aria-label='24 hour time'
                onClick={event => {
                  event.stopPropagation();
                  setUse12Hour(false);
                }}
              >
                24
              </button>
              <button
                type='button'
                className={cn(tzc.hourFormatOption, {[tzc.hourFormatOptionPressed]: use12Hour})}
                aria-pressed={use12Hour}
                aria-label='12 hour with AM and PM'
                onClick={event => {
                  event.stopPropagation();
                  setUse12Hour(true);
                }}
              >
                12
              </button>
            </div>
          </div>
        </ListSubheader>

        <MenuItem
          className={tzc.menuItem}
          onClick={() => {
            toggleSelectAll();
          }}
        >
          <Checkbox
            size='small'
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
            className={tzc.menuCheckbox}
            inputProps={{'aria-label': 'Select all timezones'}}
            onClick={event => {
              event.stopPropagation();
              toggleSelectAll();
            }}
          />
          Select all
        </MenuItem>

        <Divider className={tzc.menuDivider} />

        {menuTimezones.map(tz => (
          <MenuItem
            key={tz.id}
            className={tzc.menuItem}
            onClick={() => {
              toggleClock(tz.id);
            }}
          >
            <Checkbox
              size='small'
              checked={selectedIdSet.has(tz.id)}
              className={tzc.menuCheckbox}
              inputProps={{'aria-label': tz.label}}
              onClick={event => {
                event.stopPropagation();
                toggleClock(tz.id);
              }}
            />
            {tz.label}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}

export const privateApi = {
  storageKey,
  hour12StorageKey,
  safeParseBoolean,
  safeParseSelected,
  sanitizeSelectedIds,
  exchangeFromLabel,
  moveItem,
  formatTime,
};
